import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import type { BimWorkOrderOtherPriceData } from 'gql/graphql';
import { BimContractOtherStatusEnum, ContractStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Checkbox, Button, IconExpand } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';

import { BimWorkOrderOtherTableRowMenu } from '../BimWorkOrderTableRowMenu';
import { getBimWorkOrderOtherTableRowMenuItemsAvailability } from '../BimWorkOrderTableRowMenu/utils/getBimWorkOrderOtherTableRowMenuItemsAvailability';

export const BimOtherMenuRenderer =
    ({
        constructionTypeId,
        checkedRows,
        statusWorkOrder,
        checkRowHandler,
        openSingleDeleteModalHandler,
        toggleTotalDetails,
        refreshWorkOrderOtherPriceList,
        isFrameContract,
        hasOtherActsKc2,
        contractStatus,
        isCheckDisabled,
        orderRules,
        isVersionPage,
    }: {
        constructionTypeId?: number;
        checkedRows: Record<number, boolean>;
        statusWorkOrder: WorkOrderStatusEnum;
        checkRowHandler: ({ id, state }: { id: number; state: BimWorkOrderOtherPriceData | null | undefined }) => void;
        openSingleDeleteModalHandler: () => void;
        refreshWorkOrderOtherPriceList: () => void;
        toggleTotalDetails: () => void;
        isFrameContract: boolean;
        hasOtherActsKc2: boolean;
        contractStatus?: ContractStatusEnum;
        isCheckDisabled: boolean;
        orderRules?: IUseAccess['orderBim'];
        isVersionPage?: boolean;
    }) =>
    // eslint-disable-next-line react/display-name
    (params: ICellRendererParams<BimWorkOrderOtherPriceData & { isGroupTitle?: boolean }>) => {
        const { data, node } = params;

        if (data?.isGroupTitle) {
            return '';
        }

        const isBottomTotalRow = node.rowPinned === 'bottom';

        const handleCheckbox =
            (id: number) =>
            ({ checked }: { checked: boolean }) => {
                if (checked) {
                    checkRowHandler({ id, state: data });
                } else {
                    checkRowHandler({ id, state: null });
                }
            };

        const openDeleteModalAndCheckRow = () => {
            checkRowHandler({ id: data?.id, state: data });
            openSingleDeleteModalHandler();
        };

        const availabilityMap = getBimWorkOrderOtherTableRowMenuItemsAvailability({
            statusWorkOrder,
            isFrameContract,
            dataRow: node?.data,
        });

        const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
        const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;
        const isDisabledFromContractStatus = isContractStatusDelayed || isContractStatusCompleted;

        const conditionMenu = !availabilityMap.Delete;
        const otherStatus = data?.status === BimContractOtherStatusEnum.ContractCondition;
        const isWorkOrderStatus =
            statusWorkOrder === WorkOrderStatusEnum.Completed || statusWorkOrder === WorkOrderStatusEnum.Delayed;
        const isActPrice = Boolean(node?.data?.isActKC2Price);
        const isExcludedRow = node?.data?.isExcludedRow;

        const isHiddenToolTip =
            !hasOtherActsKc2 || isExcludedRow
                ? true
                : !conditionMenu || otherStatus || isWorkOrderStatus || isVersionPage || !isActPrice;
        const isDisabledCheckbox =
            conditionMenu ||
            isDisabledFromContractStatus ||
            isCheckDisabled ||
            !orderRules?.canEdit ||
            !node?.data?.hasEmptyActAcceptedCost ||
            !node?.data?.hasEmptyActPlanCost ||
            isExcludedRow ||
            isVersionPage;

        return !isBottomTotalRow ? (
            <TooltipWrapper
                isHidden={isHiddenToolTip}
                size="m"
                message="Имеется фактическое выполнение. Для того, чтобы удалить строку необходимо очистить значения в ячейках Акта"
                isInteractive={false}
            >
                <Checkbox
                    disabled={isDisabledCheckbox}
                    checked={checkedRows[data?.id]}
                    onChange={handleCheckbox(data?.id)}
                    style={{ padding: '4px' }}
                />
                <BimWorkOrderOtherTableRowMenu
                    dataRow={node?.data}
                    constructionTypeId={constructionTypeId}
                    openSingleDeleteModalHandler={openDeleteModalAndCheckRow}
                    refreshWorkOrderOtherPriceList={refreshWorkOrderOtherPriceList}
                    statusWorkOrder={statusWorkOrder}
                    isFrameContract={isFrameContract}
                />
            </TooltipWrapper>
        ) : (
            <Button onClick={toggleTotalDetails} iconLeft={IconExpand} onlyIcon view="clear" size="xs" />
        );
    };

BimOtherMenuRenderer.displayName = 'BimOtherMenuRenderer';
